import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, TrendingUp, TrendingDown } from "lucide-react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { useEffect } from "react";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import type { KpiId, KpiData } from "./dashboard.types";

// ── KPI Display Config ───────────────────────────────────────────

const KPI_LABEL_KEYS: Record<KpiId, string> = {
  activeAudits: "dashboard:kpi.activeAudits",
  completedAudits: "dashboard:kpi.completed",
  openFindings: "dashboard:kpi.openFindings",
  missingEvidence: "dashboard:kpi.missingEvidence",
  fixRate: "dashboard:kpi.fixRate",
  upcomingDeadlines: "dashboard:kpi.upcomingDeadlines",
};

// ── TrendText ───────────────────────────────────────────────────

function AnimatedTrendValue({ value }: { value: number }) {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v) => Math.round(Math.abs(v)));

  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 0.8,
      ease: "easeOut",
    });
    return controls.stop;
  }, [motionValue, value]);

  return <motion.span>{rounded}</motion.span>;
}

function TrendInline({
  trend,
  invertTrend: _invertTrend,
}: {
  trend: number;
  invertTrend?: boolean;
}) {
  if (trend === 0) return null;
  const isPositive = trend > 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;

  // Arrow color based on trend direction:
  // positive → green, negative <10% → yellow, negative >=10% → red
  let arrowColor: string;
  if (isPositive) {
    arrowColor = "text-emerald-500";
  } else if (Math.abs(trend) < 10) {
    arrowColor = "text-amber-500";
  } else {
    arrowColor = "text-red-500";
  }

  return (
    <motion.span
      className="inline-flex items-center gap-1 text-xs font-medium text-primary"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Icon size={14} className={arrowColor} />
      <AnimatedTrendValue value={trend} />%
    </motion.span>
  );
}

// ── Grid Helpers ─────────────────────────────────────────────────

const GRID_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
  6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
};

// ── KPI Skeleton Card ────────────────────────────────────────────

function KpiSkeletonCard() {
  return (
    <div className="rounded-2xl bg-surface px-5 py-3.5 flex flex-col gap-1 shadow-sm animate-pulse">
      <div className="h-4 w-24 bg-card-hover rounded" />
      <div className="flex items-baseline gap-2 pt-1">
        <div className="h-8 w-12 bg-card-hover rounded" />
        <div className="h-3 w-14 bg-card-hover rounded" />
      </div>
    </div>
  );
}

// ── SortableKpiCard ──────────────────────────────────────────────

interface SortableKpiCardProps {
  kpi: KpiData;
  children: React.ReactNode;
}

function SortableKpiCard({ kpi, children }: SortableKpiCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: kpi.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative rounded-2xl bg-surface px-5 py-3.5 flex flex-col gap-1 shadow-sm transition-shadow duration-200 hover:shadow-md ${
        isDragging ? "opacity-50 ring-2 ring-accent/30" : ""
      }`}
    >
      {/* Drag handle — top right, visible on hover */}
      <button
        type="button"
        className="drag-handle absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-muted hover:text-secondary touch-none"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>

      {children}
    </div>
  );
}

// ── KpiCards ─────────────────────────────────────────────────────

interface KpiCardsProps {
  data: KpiData[] | null;
  isLoading: boolean;
  visibleKpis: KpiId[];
  kpiOrder: KpiId[];
  onKpiOrderChange: (newOrder: KpiId[]) => void;
  timeRange?: string;
}

export function KpiCards({
  data,
  isLoading,
  visibleKpis,
  kpiOrder,
  onKpiOrderChange,
  timeRange,
}: KpiCardsProps) {
  const { t } = useTranslation(["dashboard"]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const visibleSet = new Set(visibleKpis);
  const visibleCount = visibleKpis.length;

  const kpiData = data ?? [];

  // Ordered visible KPIs for display — use type predicate to avoid cast
  const filteredKpis: KpiData[] = kpiOrder
    .filter((id) => visibleSet.has(id))
    .map((id) => kpiData.find((kpi) => kpi.id === id))
    .filter((kpi): kpi is NonNullable<typeof kpi> => kpi != null);

  const count = filteredKpis.length;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Reorder within the full kpiOrder array (not just visible subset)
    const oldIndex = kpiOrder.indexOf(active.id as KpiId);
    const newIndex = kpiOrder.indexOf(over.id as KpiId);
    onKpiOrderChange(arrayMove(kpiOrder, oldIndex, newIndex));
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`grid ${GRID_CLASS[visibleCount] ?? "grid-cols-3"} gap-3`}
        >
          {Array.from({ length: visibleCount }).map((_, i) => (
            <KpiSkeletonCard key={`kpi-skeleton-${i.toString()}`} />
          ))}
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredKpis.map((k) => k.id)}
              strategy={horizontalListSortingStrategy}
            >
              <div
                className={`grid ${GRID_CLASS[count] ?? "grid-cols-3"} gap-3`}
              >
                {filteredKpis.map((kpi, index) => (
                  <SortableKpiCard key={kpi.id} kpi={kpi}>
                    <motion.div
                      key={`${kpi.id}-${timeRange}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ ...springs.snappy, delay: index * 0.05 }}
                      className="flex flex-col gap-2"
                    >
                      {/* Label */}
                      <p className="text-[13px] text-muted leading-snug">
                        {
                          t(
                            KPI_LABEL_KEYS[
                              kpi.id
                            ] as "dashboard:kpi.activeAudits",
                          ) as string
                        }
                      </p>

                      {/* Value + inline trend */}
                      <div className="flex items-baseline gap-2 pt-1">
                        <AnimatedNumber
                          value={kpi.value}
                          className="text-3xl font-bold text-primary tabular-nums"
                        />
                        {kpi.unit && (
                          <span className="text-sm font-medium text-secondary">
                            {kpi.unit}
                          </span>
                        )}
                        {kpi.trend != null && (
                          <TrendInline
                            trend={kpi.trend}
                            invertTrend={kpi.invertTrend}
                          />
                        )}
                      </div>
                    </motion.div>
                  </SortableKpiCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
